// Le suivi des dérogations en 4 étapes (D-137) : logique pure, sans réseau ni écran.
// Nous demandons : pré-demande · réponse · saisie FBI · FFBB ; un autre club nous demande : reçue · notre réponse ·
// saisie FBI (par lui) · FFBB. La saisie FBI n'attend jamais la réponse ; le délai « sans réponse » (3 jours par
// défaut, de 2 à 7, réglé par le club) n'est qu'un rappel : l'étape passe en hachuré et « Relancer » est proposé.
import { dayLong, dayShort, hm, lowerFirst, ofClub, slotTitle, type PreStatus, type Slot } from './derogation';

const fmt = (iso: string, o: Intl.DateTimeFormatOptions, tz?: string) =>
  new Intl.DateTimeFormat('fr-FR', { ...o, ...(tz ? { timeZone: tz } : {}) }).format(new Date(iso));

/** Une étape : faite (vert), à toi (couleur du club), chez quelqu'un d'autre (orange), sans réponse (hachuré), refusée (rouge),
 *  « ça leur pose problème » (orange plein, D-146). */
export type StepState = 'done' | 'now' | 'wait' | 'skip' | 'ko' | 'pb' | '';
export const OUR_STEPS = ['Pré-demande', 'Réponse', 'Saisie FBI', 'FFBB'] as const;
export const THEIR_STEPS = ['Reçue', 'Notre réponse', 'Saisie FBI', 'FFBB'] as const;
/** D-146 : « Les prévenir », 3 étapes (prévenu · saisie FBI · FFBB). */
export const PREV_STEPS = ['Prévenu', 'Saisie FBI', 'FFBB'] as const;
export const WAIT_DEFAULT = 3;

/** Ce que le suivi lit d'une dérogation. */
export interface DerogTrack {
  direction: 'out' | 'in'; status: PreStatus; kind: 'moved' | 'swap'; created_at: string;
  /** D-146 : « Demander leur accord » (par défaut) ou « Les prévenir ». */
  mode?: 'accord' | 'prevenir';
  /** 'ack' (« Bien reçu ») et 'problem' (« Ça nous pose problème ») : les réponses à « Les prévenir ». */
  answer: 'accept' | 'refuse' | 'counter' | 'ack' | 'problem' | null; answered_at: string | null;
  notify_coach: boolean; coach_opinion: 'yes' | 'no' | null; our_answer: 'accept' | 'refuse' | null;
  filed_at: string | null; confirmed_at: string | null; rejected_at: string | null;
}

/** Jours entiers écoulés (calendrier, heure locale) : « depuis 5 jours ». */
export function daysSince(iso: string, now: Date = new Date()): number {
  const d = new Date(iso), a = new Date(d.getFullYear(), d.getMonth(), d.getDate()), b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** D-146 : « Les prévenir » (pas d'accord attendu). */
export const isPrev = (d: Pick<DerogTrack, 'mode'>) => d.mode === 'prevenir';

/** Sans réponse au-delà du délai du club : un rappel, jamais un verrou. « Les prévenir » n'attend rien (ni délai, ni relance). */
export function noAnswer(d: DerogTrack, now: Date = new Date(), waitDays = WAIT_DEFAULT): boolean {
  return d.direction === 'out' && !isPrev(d) && !d.answered_at && d.status !== 'cancelled'
    && (now.getTime() - new Date(d.created_at).getTime()) >= waitDays * 86_400_000;
}

/** Les 4 étapes d'une dérogation. */
export function derogStates(d: DerogTrack, now: Date = new Date(), waitDays = WAIT_DEFAULT): [StepState, StepState, StepState, StepState] {
  const ffbb: StepState = d.status === 'confirmed' ? 'done' : d.status === 'rejected' ? 'ko' : '';
  if (d.direction === 'in') {
    const ours: StepState = d.our_answer === 'accept' || (!d.our_answer && (d.status === 'confirmed' || d.status === 'filed')) ? 'done'
      : d.our_answer === 'refuse' ? 'ko' : d.notify_coach && !d.coach_opinion ? 'wait' : 'now';
    const fbi: StepState = d.filed_at || d.status === 'confirmed' ? 'done' : d.our_answer === 'accept' && d.status !== 'rejected' ? 'wait' : '';
    return ['done', ours, fbi, ffbb || (fbi === 'done' ? 'wait' : '')];
  }
  const answer: StepState = d.answer === 'accept' ? 'done' : d.answer === 'refuse' ? 'ko'
    : d.answer === 'counter' ? (d.status === 'countered' ? 'now' : 'done')
    : noAnswer(d, now, waitDays) || d.status === 'confirmed' || d.status === 'rejected' ? 'skip' : 'wait';
  const fbi: StepState = d.filed_at || d.status === 'confirmed' || d.status === 'rejected' ? 'done'
    : answer === 'done' || answer === 'skip' || answer === 'ko' ? 'now' : '';
  return ['done', answer, fbi, ffbb || (d.status === 'filed' ? 'wait' : '')];
}

/** D-146 : les 3 étapes de « Les prévenir » : prévenu (orange plein si « ça nous pose problème »), saisie FBI (à toi tout de
 *  suite, rien à attendre), FFBB. */
export function prevStates(d: DerogTrack): [StepState, StepState, StepState] {
  const first: StepState = d.answer === 'problem' ? 'pb' : 'done';
  if (d.status === 'confirmed') return [first, 'done', 'done'];
  if (d.status === 'rejected') return [first, 'done', 'ko'];
  if (d.status === 'filed') return [first, 'done', 'wait'];
  if (d.status === 'cancelled') return [first, '', ''];
  return [first, d.answer === 'problem' ? '' : 'now', ''];
}

/** La barre d'une dérogation, quelle qu'elle soit : 3 étapes (prévenir) ou 4 (une demande retirée s'arrête où elle en était). */
export function stepStates(d: DerogTrack, now: Date = new Date(), waitDays = WAIT_DEFAULT): StepState[] {
  if (isPrev(d)) return prevStates(d);
  const s = derogStates(d, now, waitDays);
  return d.status === 'cancelled' ? [s[0], d.answered_at ? s[1] : '', '', ''] : s;
}
export const stepLabelsOf = (d: Pick<DerogTrack, 'direction' | 'mode'>): readonly string[] => (isPrev(d) ? PREV_STEPS : d.direction === 'in' ? THEIR_STEPS : OUR_STEPS);

/** « Saisi sur FBI » possible : jamais refusé faute de réponse (en attente, refusée, contre-proposée, acceptée) ; une demande
 *  reçue, une fois acceptée (« Fait sur FBI », facultatif). */
export const canFile = (d: Pick<DerogTrack, 'direction' | 'status'>) =>
  (d.direction === 'out' ? ['pending', 'accepted', 'refused', 'countered'].includes(d.status) : d.status === 'accepted');

/** Où la ranger : à faire d'abord, en attente, confirmée, close. */
export type DerogPhase = 'todo' | 'waiting' | 'confirmed' | 'closed';
export function derogPhase(d: DerogTrack, now: Date = new Date(), waitDays = WAIT_DEFAULT): DerogPhase {
  if (d.status === 'confirmed') return 'confirmed';
  if (d.status === 'rejected' || d.status === 'cancelled' || (d.direction === 'in' && d.status === 'refused')) return 'closed';
  if (d.direction === 'in') return d.status === 'pending' ? 'todo' : 'waiting';
  // D-146 : « Les prévenir » est à faire jusqu'à la saisie FBI (avec ou sans « Bien reçu », ou « ça nous pose problème »).
  if (isPrev(d)) return d.status === 'filed' ? 'waiting' : 'todo';
  const [, answer, fbi] = derogStates(d, now, waitDays);
  return answer === 'now' || fbi === 'now' ? 'todo' : 'waiting';
}

/** « dim. 15 nov. 13h » */
export const dayHm = (iso: string, tz?: string) => `${dayShort(iso, tz).replace(/ 1 /, ' 1er ')} ${hm(iso, tz)}`;

/** « AS Larbrelle » → « Larbrelle » quand on dit « à … » d'une ville (sigle de club retiré). */
export function shortPlace(club: string): string {
  const t = club.trim().replace(/^(ASPTT|ASC|AS|BC|US|ES|JA|CS|SC|AL|Union|Basket Club)\s+(de\s+|d’|d')?/i, '');
  return t || club.trim();
}

/** Ce qui est demandé, en une ligne : « dim. 15 nov. 13h → dim. 29 nov. 15h30 », ou l'inversion (datée ou non). */
export function askedText(d: { kind: 'moved' | 'swap'; old_starts_at: string; old_location: string | null; slots: Slot[]; pair_slot?: Slot | null },
  o: { opponent: string; isHome: boolean | null; tz?: string }): string {
  if (d.kind === 'swap') {
    const a = d.slots[0], r = d.pair_slot;
    if (!a && !r) return 'Inversion domicile / extérieur, mêmes dates';
    const here = (home: boolean | null) => (home ? 'chez nous' : `à ${shortPlace(o.opponent)}`);
    return `Inversion avec nouvelles dates : ${[a ? `aller ${dayHm(a.starts_at, o.tz)} ${here(!o.isHome)}` : '', r ? `retour ${dayHm(r.starts_at, o.tz)} ${here(!!o.isHome)}` : '']
      .filter(Boolean).join(', ')}`;
  }
  const one = d.slots.length === 1 ? d.slots[0]! : null;
  return `${dayHm(d.old_starts_at, o.tz)} → ${d.slots.map(s => dayHm(s.starts_at, o.tz)).join(' ou ')}${one?.location && one.location !== d.old_location ? ` · ${one.location}` : ''}`;
}

/** Les dates d'une inversion datée, vues par l'autre club (page sans compte) : aller et retour, « au lieu du… ». */
export interface SwapView { starts_at: string | null; location: string | null; pair_starts_at: string | null; pair_location: string | null }
export function swapDatedLines(m: { starts_at: string; is_home: boolean | null; location: string | null }, pair: { starts_at: string; location: string | null } | null,
  s: SwapView, tz?: string): { head: string; where: string; instead: string }[] {
  // « nous » = le club qui demande : chaque match passe de l'autre côté.
  const after = (home: boolean | null) => (home ? 'chez vous' : 'chez nous');
  const before = (home: boolean | null) => (home ? 'chez nous' : 'chez vous');
  const thisAt = s.starts_at ?? m.starts_at, thisLoc = s.location ?? pair?.location ?? null;
  // Aller et retour dans l'ordre de la saison (ce match peut être le retour).
  const first = !pair || new Date(m.starts_at) <= new Date(pair.starts_at);
  const out = [{ head: `${first ? 'Aller' : 'Retour'} · ${slotTitle(thisAt, tz)}`, where: `${after(m.is_home)}${thisLoc ? `, ${thisLoc}` : ''}`,
    instead: `au lieu du ${dayShort(m.starts_at, tz)} à ${hm(m.starts_at, tz)} ${before(m.is_home)}` }];
  if (pair) {
    const pairAt = s.pair_starts_at ?? pair.starts_at, pairLoc = s.pair_location ?? m.location ?? null;
    out.push({ head: `${first ? 'Retour' : 'Aller'} · ${slotTitle(pairAt, tz)}`, where: `${after(!m.is_home)}${pairLoc ? `, ${pairLoc}` : ''}`,
      instead: `au lieu du ${dayShort(pair.starts_at, tz)} à ${hm(pair.starts_at, tz)} ${before(!m.is_home)}` });
  }
  return out;
}

/** La ligne « Club adverse » du texte pour FBI, sans accord : prévenu par la pré-demande, avec la date. */
export function fbiPrevenu(i: { opponentClub: string; sentAt: string; remindedAt?: string | null; refusedAt?: string | null; refusal?: string | null; tz?: string }): string {
  const d = (iso: string, y = true) => fmt(iso, y ? { day: '2-digit', month: '2-digit', year: 'numeric' } : { day: '2-digit', month: '2-digit' }, i.tz);
  const relance = i.remindedAt ? ` (relancé le ${d(i.remindedAt, false)})` : '';
  if (i.refusedAt) return `Club adverse : ${i.opponentClub}, prévenu le ${d(i.sentAt)} par la pré-demande Arceau${relance}, a répondu « pas possible » le ${d(i.refusedAt)}${i.refusal ? ` (« ${i.refusal} »)` : ''}.`;
  return `Club adverse : ${i.opponentClub}, prévenu le ${d(i.sentAt)} par la pré-demande Arceau${relance}, sans réponse à ce jour.`;
}

/** L'inversion datée, pour FBI : ce match et le retour, avec leurs nouvelles dates et salles. */
export function fbiSwapLines(i: { aller: Slot | null; retour: Slot | null; pairOld: string | null; isHome: boolean | null; tz?: string }): string[] {
  const long = (iso: string) => `${dayLong(iso, i.tz)} ${fmt(iso, { year: 'numeric' }, i.tz)} à ${hm(iso, i.tz)}`;
  const lines = ['Demande : inversion domicile / extérieur, avec de nouvelles dates'];
  if (i.aller) lines.push(`Ce match : ${long(i.aller.starts_at)}${i.aller.location ? `, ${i.aller.location}` : ''} (${i.isHome ? 'chez l’adversaire' : 'chez nous'})`);
  if (i.retour) lines.push(`Le retour : ${long(i.retour.starts_at)}${i.retour.location ? `, ${i.retour.location}` : ''}${i.pairOld ? ` (actuellement le ${long(i.pairOld)})` : ''}`);
  return lines;
}

/** Notre réponse à une demande reçue, à envoyer par WhatsApp (vouvoiement : l'autre club). */
export function inAnswerMessage(o: { club: string; title: string; oldIso: string; accept: boolean; what: string; note: string | null; signer: string | null; tz?: string }): string {
  const hello = `Bonjour, ici le bureau ${ofClub(o.club)}. Pour ${o.title} du ${dayShort(o.oldIso, o.tz)} : `;
  const body = o.accept ? `d’accord pour ${o.what}. Vous pouvez saisir la demande sur FBI ; si FBI nous demande notre accord, nous le donnerons.`
    : `ce n’est malheureusement pas possible${o.note ? ` (${lowerFirst(o.note)})` : ''}.`;
  return `${hello}${body}${o.accept && o.note ? ` ${o.note}` : ''} Sportivement${o.signer ? `, ${o.signer}` : ''}.`;
}

/** La salle est-elle libre ? Les occupations d'un jour (séances, matchs à domicile) autour du créneau voulu (2 h). */
export interface HallOcc { starts: string; ends: string; label: string; match: boolean }
export function hallFree(occ: HallOcc[], start: Date, minutes = 120): { free: boolean; from: Date | null; to: Date | null; next: HallOcc | null; clash: HallOcc | null } {
  const end = new Date(start.getTime() + minutes * 60_000);
  const sorted = [...occ].sort((a, b) => new Date(a.starts).getTime() - new Date(b.starts).getTime());
  const clash = sorted.find(o => new Date(o.starts) < end && start < new Date(o.ends)) ?? null;
  const before = sorted.filter(o => new Date(o.ends) <= start).pop() ?? null;
  const next = sorted.find(o => new Date(o.starts) >= end) ?? null;
  // Un match qui suit : on lui laisse 30 minutes d'échauffement.
  const to = next ? new Date(new Date(next.starts).getTime() - (next.match ? 30 * 60_000 : 0)) : null;
  return { free: !clash && (!to || to >= end), from: before ? new Date(before.ends) : null, to, next, clash };
}

// --- D-146 : les filtres de la liste ----------------------------------------------------------------------------------
/** La puce d'une dérogation : à faire maintenant, en attente de réponse (nous demandons) ou saisie FBI par eux (reçue),
 *  en attente de la FFBB, confirmée, refusée ou abandonnée. Les puces ne se recoupent pas (« À saisir sur FBI » est un
 *  sous-ensemble de « À faire maintenant », voir fbiNow). */
export type DerogChip = 'todo' | 'wait' | 'theirs' | 'ffbb' | 'ok' | 'ko';
export function derogChip(d: DerogTrack, now: Date = new Date(), waitDays = WAIT_DEFAULT): DerogChip {
  const ph = derogPhase(d, now, waitDays);
  if (ph === 'confirmed') return 'ok';
  if (ph === 'closed') return 'ko';
  if (ph === 'todo') return 'todo';
  if (d.status === 'filed') return 'ffbb';
  return d.direction === 'in' ? 'theirs' : 'wait';
}
/** « À saisir sur FBI » : à faire, et le geste attendu est la saisie (prévenus sans problème, accord reçu, sans réponse). */
export function fbiNow(d: DerogTrack, now: Date = new Date(), waitDays = WAIT_DEFAULT): boolean {
  if (d.direction !== 'out' || derogChip(d, now, waitDays) !== 'todo') return false;
  if (isPrev(d)) return d.answer !== 'problem' && d.status === 'pending';
  return d.status === 'accepted' || (d.status === 'pending' && noAnswer(d, now, waitDays));
}

/** La dérogation en cours de chaque match : la plus récente (les autres restent dans l'historique). La liste arrive la plus
 *  récente d'abord. */
export function currentOf<T>(list: T[], eventOf: (x: T) => string): T[] {
  const seen = new Set<string>();
  return list.filter(x => { const e = eventOf(x); return seen.has(e) ? false : (seen.add(e), true); });
}

/** Le week-end d'un match (la semaine du lundi au dimanche) : « Ce week-end · 31 oct.–1er nov. », « Week-end prochain ·
 *  7–8 nov. », « Week-end du 14–15 nov. », « Week-end dernier · … ». */
export function weekendLabel(iso: string, now: Date = new Date()): string {
  const monday = (x: Date) => { const d = new Date(x); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };
  const mon = monday(new Date(iso)), sat = new Date(mon), sun = new Date(mon);
  sat.setDate(sat.getDate() + 5); sun.setDate(sun.getDate() + 6);
  const n = Math.round((mon.getTime() - monday(now).getTime()) / (7 * 86_400_000));
  const dd = (d: Date) => (d.getDate() === 1 ? '1er' : String(d.getDate()));
  const mo = (d: Date) => d.toLocaleDateString('fr-FR', { month: 'short' });
  const range = sat.getMonth() === sun.getMonth() ? `${dd(sat)}–${dd(sun)} ${mo(sun)}` : `${dd(sat)} ${mo(sat)}–${dd(sun)} ${mo(sun)}`;
  return n === 0 ? `Ce week-end · ${range}` : n === 1 ? `Week-end prochain · ${range}` : n === -1 ? `Week-end dernier · ${range}` : `Week-end du ${range}`;
}

/** Chercher une équipe, un adversaire : sans accents ni majuscules. */
export const searchKey = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[’']/g, ' ').replace(/\s+/g, ' ').trim();

// --- D-146 : « Les prévenir » ---------------------------------------------------------------------------------------
/** Le seuil fixe (pas de réglage par club) : la salle seule, ou jusqu'à 1 h le même jour → « Les prévenir » proposé
 *  d'office ; sinon « Demander leur accord ». `why` : la raison, en mots, pour la feuille. */
export function proposeMode(old: { start: Date; location: string | null }, slot: { starts_at: string; location: string | null }):
  { mode: 'prevenir' | 'accord'; why: string } {
  const at = new Date(slot.starts_at);
  if (isNaN(at.getTime())) return { mode: 'accord', why: '' };
  const day = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const mins = Math.round((at.getTime() - old.start.getTime()) / 60_000);
  const hall = (slot.location ?? '').trim() !== (old.location ?? '').trim() && !!(slot.location ?? '').trim();
  if (day(at) !== day(old.start)) return { mode: 'accord', why: 'le jour change' };
  const dur = (m: number) => { const a = Math.abs(m), h = Math.floor(a / 60), r = a % 60; return h ? `${h} h${r ? ` ${String(r).padStart(2, '0')}` : ''}` : `${r} min`; };
  const shift = mins === 0 ? '' : `${dur(mins)} plus ${mins > 0 ? 'tard' : 'tôt'}`;
  if (Math.abs(mins) > 60) return { mode: 'accord', why: `${shift}, c’est plus qu’un petit changement` };
  if (!shift) return { mode: 'prevenir', why: 'la salle seule change (petit changement)' };
  return { mode: 'prevenir', why: `${shift}${hall ? ' et une autre salle' : ''}, le même jour (petit changement)` };
}

/** Le changement annoncé, en une ligne : « sam. 7 nov. 20h30 → 21h », « sam. 7 nov. 13h · Gymnase de Rivebelle au lieu de
 *  Salle Bellevue », « sam. 7 nov. 20h30 → dim. 8 nov. 11h ». */
export function prevLine(d: { old_starts_at: string; old_location: string | null; slots: Slot[] }, tz?: string): string {
  const s = d.slots[0];
  if (!s) return dayHm(d.old_starts_at, tz);
  const ymd = (iso: string) => fmt(iso, { year: 'numeric', month: '2-digit', day: '2-digit' }, tz);
  const sameDay = ymd(s.starts_at) === ymd(d.old_starts_at);
  const moved = new Date(s.starts_at).getTime() !== new Date(d.old_starts_at).getTime();
  const hall = !!s.location && s.location !== d.old_location;
  if (!moved && hall) return `${dayHm(d.old_starts_at, tz)} · ${s.location}${d.old_location ? ` au lieu de ${d.old_location}` : ''}`;
  return `${dayHm(d.old_starts_at, tz)} → ${sameDay ? hm(s.starts_at, tz) : dayHm(s.starts_at, tz)}${hall ? ` · ${s.location}` : ''}`;
}

/** « Hélène Roux, secrétariat » → « Hélène Roux (secrétariat) » (le nom et le rôle, un seul champ sur la page). */
export function whoText(name: string | null): string | null {
  const t = (name ?? '').trim();
  if (!t) return null;
  const i = t.lastIndexOf(',');
  return i > 0 && i < t.length - 1 ? `${t.slice(0, i).trim()} (${t.slice(i + 1).trim()})` : t;
}

/** La ligne « Club adverse » du texte pour FBI d'un « prévenir » : prévenu par Arceau, « bien reçu » ou le problème signalé. */
export function fbiPrevLine(i: { opponentClub: string; sentAt: string; answer: 'ack' | 'problem' | null; answeredAt: string | null;
  answerName: string | null; message: string | null; tz?: string }): string {
  const d = (iso: string) => fmt(iso, { day: '2-digit', month: '2-digit', year: 'numeric' }, i.tz);
  const head = `Club adverse : ${i.opponentClub}, prévenu le ${d(i.sentAt)} par Arceau (petit changement annoncé)`;
  const who = (i.answerName ?? '').trim();
  if (i.answer === 'ack' && i.answeredAt) return `${head}, « bien reçu » le ${d(i.answeredAt)}${who ? ` (${who})` : ''}.`;
  if (i.answer === 'problem' && i.answeredAt) return `${head}, a signalé un problème le ${d(i.answeredAt)}${who ? ` (${who})` : ''}${i.message ? ` : « ${i.message} »` : ''}.`;
  return `${head}.`;
}

/** Le message « nous allons saisir ce changement sur FBI », à envoyer par WhatsApp ou SMS (vouvoiement : l'autre club). */
export function prevShareMessage(o: { club: string; title: string; oldIso: string; oldLocation: string | null; slot: Slot; url: string; tz?: string }): string {
  const sameDay = dayShort(o.slot.starts_at, o.tz) === dayShort(o.oldIso, o.tz);
  const moved = new Date(o.slot.starts_at).getTime() !== new Date(o.oldIso).getTime();
  const hall = !!o.slot.location && o.slot.location !== o.oldLocation;
  const what = !moved && hall ? `le match se joue à ${o.slot.location} au lieu de ${o.oldLocation ?? 'la salle prévue'}, même heure`
    : sameDay ? `coup d’envoi à ${hm(o.slot.starts_at, o.tz)} au lieu de ${hm(o.oldIso, o.tz)}${hall ? `, à ${o.slot.location}` : ', même salle'}`
    : `le match passe au ${dayShort(o.slot.starts_at, o.tz)} à ${hm(o.slot.starts_at, o.tz)}${hall ? `, à ${o.slot.location}` : ', même salle'}`;
  return `Bonjour, ici le bureau ${ofClub(o.club)}. Petit changement pour ${o.title} du ${dayShort(o.oldIso, o.tz)} : ${what}. `
    + `Nous allons le saisir sur FBI. Bien reçu, ou un souci ? Répondez en 1 clic, sans compte : ${o.url}`;
}
